import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Switch,
  Tabs,
} from 'antd';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { faqService, FAQ } from '../services/faqService';

const { Title } = Typography;
const { TextArea } = Input;

// Simplified - drag and drop can be added later if needed

const FAQs: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState<FAQ | null>(null);
  const [form] = Form.useForm();
  const quillRef = useRef<Quill | null>(null);
  const quillContainerRef = useRef<HTMLDivElement>(null);
  const [previewContent, setPreviewContent] = useState<string>('');

  useEffect(() => {
    fetchFAQs();
  }, []);

  // Initialize Quill editor when modal opens
  useEffect(() => {
    if (modalVisible && quillContainerRef.current) {
      // Clean up existing Quill instance
      if (quillRef.current) {
        quillRef.current = null;
      }

      // Create new Quill instance
      quillRef.current = new Quill(quillContainerRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link', 'image'],
            ['blockquote', 'code-block'],
            ['clean']
          ]
        },
        placeholder: 'Start writing your answer here...',
      });

      // Set initial content if editing
      if (editingFAQ?.answer) {
        quillRef.current.root.innerHTML = editingFAQ.answer;
        setPreviewContent(editingFAQ.answer);
      }

      // Sync Quill content with form and preview
      quillRef.current.on('text-change', () => {
        const content = quillRef.current?.root.innerHTML || '';
        form.setFieldsValue({ answer: content });
        setPreviewContent(content);
      });
    }

    return () => {
      // Cleanup on unmount
      if (quillRef.current) {
        quillRef.current = null;
      }
    };
  }, [modalVisible, editingFAQ, form]);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      // 'all' so the admin sees BOTH active and inactive FAQs — otherwise a
      // toggled-off FAQ disappears from the list and can't be re-enabled.
      const response = await faqService.getFAQs(undefined, 'all');
      setFaqs(response.data || []);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingFAQ(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setPreviewContent('');
    setModalVisible(true);
  };

  const handleEdit = (faq: FAQ) => {
    setEditingFAQ(faq);
    form.setFieldsValue(faq);
    setPreviewContent(faq.answer || '');
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      // Get content from Quill editor
      const answer = quillRef.current?.root.innerHTML || values.answer || '';
      
      if (!answer || answer.trim() === '<p><br></p>') {
        message.error('Please enter FAQ answer');
        return;
      }

      const faqData = {
        ...values,
        answer,
      };

      if (editingFAQ) {
        await faqService.updateFAQ(editingFAQ._id, faqData);
        message.success('FAQ updated successfully');
      } else {
        await faqService.createFAQ(faqData);
        message.success('FAQ created successfully');
      }
      setModalVisible(false);
      form.resetFields();
      setPreviewContent('');
      if (quillRef.current) {
        quillRef.current.root.innerHTML = '';
      }
      fetchFAQs();
    } catch (error: any) {
      message.error(error.message || 'Failed to save FAQ');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await faqService.toggleFAQStatus(id);
      message.success(`FAQ ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      fetchFAQs();
    } catch (error: any) {
      message.error(error.message || 'Failed to toggle FAQ status');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await faqService.deleteFAQ(id);
      message.success('FAQ deleted successfully');
      fetchFAQs();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete FAQ');
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newFaqs = [...faqs];
    [newFaqs[index - 1], newFaqs[index]] = [newFaqs[index], newFaqs[index - 1]];
    await updateOrders(newFaqs);
  };

  const handleMoveDown = async (index: number) => {
    if (index === faqs.length - 1) return;
    const newFaqs = [...faqs];
    [newFaqs[index], newFaqs[index + 1]] = [newFaqs[index + 1], newFaqs[index]];
    await updateOrders(newFaqs);
  };

  const updateOrders = async (newFaqs: FAQ[]) => {
    try {
      const faqOrders = newFaqs.map((faq, index) => ({
        id: faq._id,
        order: index,
      }));
      await faqService.reorderFAQs(faqOrders);
      setFaqs(newFaqs);
      message.success('FAQs reordered successfully');
    } catch (error: any) {
      message.error(error.message || 'Failed to reorder FAQs');
      fetchFAQs(); // Revert on error
    }
  };

  // const handleDragEnd = (event: any) => {
  //   const { active, over } = event;
  //   if (active.id !== over.id) {
  //     const oldIndex = faqs.findIndex((faq) => faq._id === active.id);
  //     const newIndex = faqs.findIndex((faq) => faq._id === over.id);
  //     const newFaqs = arrayMove(faqs, oldIndex, newIndex);
  //     updateOrders(newFaqs);
  //   }
  // };

  const columns = [
    {
      title: 'Order',
      key: 'order',
      width: 100,
      render: (_: any, record: FAQ, index: number) => (
        <Space>
          <Button
            type="text"
            icon={<UpOutlined />}
            onClick={() => handleMoveUp(index)}
            disabled={index === 0}
            size="small"
          />
          <Button
            type="text"
            icon={<DownOutlined />}
            onClick={() => handleMoveDown(index)}
            disabled={index === faqs.length - 1}
            size="small"
          />
          <span>{record.order}</span>
        </Space>
      ),
    },
    {
      title: 'Question',
      dataIndex: 'question',
      key: 'question',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => category ? <Tag>{category}</Tag> : '-',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (isActive: boolean, record: FAQ) => (
        <Space>
          <Switch
            checked={isActive}
            onChange={() => handleToggleStatus(record._id, isActive)}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
          <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: FAQ) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this FAQ?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>FAQs</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchFAQs}>
                Refresh
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Add FAQ
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={faqs}
          rowKey="_id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingFAQ ? '✏️ Edit FAQ' : '✨ Create New FAQ'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setPreviewContent('');
          if (quillRef.current) {
            quillRef.current.root.innerHTML = '';
          }
        }}
        onOk={() => form.submit()}
        width={900}
        style={{ top: 20 }}
        okText={editingFAQ ? 'Update FAQ' : 'Create FAQ'}
        cancelText="Cancel"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item 
            name="question" 
            label="Question" 
            rules={[{ required: true, message: 'Please enter FAQ question' }]}
          >
            <Input 
              maxLength={500} 
              placeholder="Enter the question"
              size="large"
            />
          </Form.Item>

          <Form.Item label="Answer" required>
            <Tabs
              defaultActiveKey="editor"
              items={[
                {
                  key: 'editor',
                  label: '✏️ Editor',
                  children: (
                    <div style={{ border: '1px solid #d9d9d9', borderRadius: '4px', backgroundColor: '#fff' }}>
                      <div ref={quillContainerRef} style={{ minHeight: '300px' }} />
                    </div>
                  ),
                },
                {
                  key: 'preview',
                  label: '👁️ Preview',
                  children: (
                    <div 
                      style={{ 
                        border: '1px solid #d9d9d9', 
                        borderRadius: '4px',
                        padding: '20px',
                        minHeight: '300px',
                        backgroundColor: '#fff'
                      }}
                      dangerouslySetInnerHTML={{ __html: previewContent || '<p style="color: #999; text-align: center; padding: 50px;">Start typing to see preview...</p>' }}
                    />
                  ),
                },
              ]}
            />
            <Form.Item name="answer" hidden>
              <Input />
            </Form.Item>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category" label="Category">
                <Input placeholder="Optional category for grouping" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="order" label="Display Order" initialValue={0}>
                <Input type="number" placeholder="0" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="isActive" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" /> FAQ is active
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FAQs;

